import { TZDateMini } from '@date-fns/tz';
import { endOfYear, isSameYear, startOfYear } from 'date-fns';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { isTimeValue } from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import { isOutOfBounds } from '../../../core/time/validation.js';
import type { ViewItem } from '../components/CalendarItem.js';
import type { UseCalendarItemProps } from '../types/shared-types.js';
import { useCalendarContext } from './useCalendarContext.js';
import { useFocus } from './useFocus.js';

/** Custom hook that retrieves properties from the year that is passed in. */
export function useYear({ date, itemRef }: UseCalendarItemProps): ViewItem {
  const { state, dispatch } = useCalendarContext();
  const configuredTimezone = getTimezone();

  const value = useCalendarStore((store) => store.calendarValue);
  const min = useCalendarStore((store) => store.min);
  const max = useCalendarStore((store) => store.max);

  const intl = useIntl();

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select year',
      id: 'pRBtzsvqzp1/QqAV',
      description: 'Aria label to add to each year button',
    });

    return `${formatMessage} ${date.getFullYear().toString()}`;
  }, [date, intl]);

  const isCurrent = useMemo(
    () => isSameYear(date, state.today),
    [date, state.today],
  );

  const isFocused = useMemo(
    () => isSameYear(date, state.activeDate),
    [state.activeDate, date],
  );

  const isSelected = useMemo(() => {
    const compareYear = date.getFullYear();
    if (!value) {
      return false;
    }

    if (isTimeValue(value)) {
      const selectedYear = value
        ? new TZDateMini(value.absoluteDate, configuredTimezone).getFullYear()
        : null;

      return compareYear === selectedYear;
    }

    const { from, to } = value;

    const fromYear = from
      ? new TZDateMini(from.absoluteDate, configuredTimezone).getFullYear()
      : null;
    const toYear = to
      ? new TZDateMini(to.absoluteDate, configuredTimezone).getFullYear()
      : null;

    return fromYear === compareYear || toYear === compareYear;
  }, [configuredTimezone, value, date]);

  const isDisabled = useMemo(() => {
    const startDay = startOfYear(date);
    const endDay = endOfYear(date);

    return !!(
      isOutOfBounds(startDay, undefined, max) || isOutOfBounds(endDay, min)
    );
  }, [min, max, date]);
  const { tabIndex } = useFocus({ isFocused, itemRef });

  /** Handle click on a year in the month view. */
  function onClick() {
    dispatch({
      type: 'activeDateChange',
      payload: { activeDate: date, changeToView: 'month' },
    });

    dispatch({ type: 'shouldUpdateFocusFromKeydown', payload: false });
  }

  return {
    ariaLabel,
    tabIndex,
    isCurrent,
    isSelected,
    isDisabled,
    onClick,
  };
}
